import { Routes } from '@angular/router';
import { YardComponent } from './yard/yard.component';
import { PermissionGuard } from '../security/permission.guard';
import { app_permissions } from '../security/app-permissions';
import { EnterpriseSubscriptionGuard } from '../security/enterprise-subscription.guard';
import { ContainerCaseDetailComponent } from './container-case-detail/container-case-detail.component';
import { ContainerTrackingOverviewComponent } from './container-tracking-overview/container-tracking-overview.component';
import { ContainerCaseEditorComponent } from './container-case-editor/container-case-editor.component';
import { ContainerTrackingDashboardComponent } from './container-tracking-dashboard/container-tracking-dashboard.component';
import { ContainerStorageLegOperationsComponent } from './container-storage-leg-operations/container-storage-leg-operations.component';
import { YardInventoryComponent } from './yard-inventory/yard-inventory.component';
import { YardOperationsComponent } from './yard-operations/yard-operations.component';
import { YardSetupMasterListComponent } from './yard-setup-master/yard-setup-master-list.component';
import { YardSetupMasterFormComponent } from './yard-setup-master/yard-setup-master-form.component';
import { ContainerBookingWorkspaceComponent } from './container-booking-workspace/container-booking-workspace.component';

export const containerTrackingRoutes: Routes = [
    {
        path: 'container-tracking',
        pathMatch: 'full',
        redirectTo: 'container-tracking/overview'
    },
    {
        path: 'container-tracking/overview',
        component: ContainerTrackingDashboardComponent,
        canActivate: [EnterpriseSubscriptionGuard, PermissionGuard],
        data: {
            permission: app_permissions.container_tracking.dashboard.view,
            scope: 'ANY',
            enterpriseModules: ['CONTAINER_TRACKING'],
            enterpriseFeatureLabel: 'Container Tracking'
        }
    },
    {
        path: 'container-tracking/setup',
        component: YardComponent,
        canActivate: [EnterpriseSubscriptionGuard, PermissionGuard],
        data: {
            permission: app_permissions.container_tracking.yards.view,
            enterpriseModules: ['CONTAINER_TRACKING'],
            enterpriseFeatureLabel: 'Container Tracking'
        }
    },
    ...(['facilities', 'areas', 'yard-slots', 'charge-rules'] as const).flatMap(segment => {
        const master = segment === 'yard-slots' ? 'slots' : segment;
        return [
            {
                path: `container-tracking/setup/${segment}`,
                component: YardSetupMasterListComponent,
                canActivate: [EnterpriseSubscriptionGuard, PermissionGuard],
                data: {
                    master,
                    permission: app_permissions.container_tracking.yards.view,
                    enterpriseModules: ['CONTAINER_TRACKING'],
                    enterpriseFeatureLabel: 'Container Tracking'
                }
            },
            {
                path: `container-tracking/setup/${segment}/add`,
                component: YardSetupMasterFormComponent,
                canActivate: [EnterpriseSubscriptionGuard, PermissionGuard],
                data: {
                    master,
                    permission: app_permissions.container_tracking.yards.create,
                    enterpriseModules: ['CONTAINER_TRACKING'],
                    enterpriseFeatureLabel: 'Container Tracking'
                }
            },
            {
                path: `container-tracking/setup/${segment}/:id/edit`,
                component: YardSetupMasterFormComponent,
                canActivate: [EnterpriseSubscriptionGuard, PermissionGuard],
                data: {
                    master,
                    permission: app_permissions.container_tracking.yards.edit,
                    enterpriseModules: ['CONTAINER_TRACKING'],
                    enterpriseFeatureLabel: 'Container Tracking'
                }
            }
        ];
    }),
    {
        path: 'container-tracking/setup/execution-values',
        component: YardSetupMasterListComponent,
        canActivate: [EnterpriseSubscriptionGuard, PermissionGuard],
        data: {
            master: 'execution-values',
            permission: app_permissions.container_tracking.yards.view,
            enterpriseModules: ['CONTAINER_TRACKING'],
            enterpriseFeatureLabel: 'Container Tracking'
        }
    },
    {
        path: 'container-tracking/inventory',
        component: YardInventoryComponent,
        canActivate: [EnterpriseSubscriptionGuard, PermissionGuard],
        data: {
            permission: app_permissions.container_tracking.yards.view,
            enterpriseModules: ['CONTAINER_TRACKING'],
            enterpriseFeatureLabel: 'Container Tracking'
        }
    },
    {
        path: 'container-tracking/occupancy',
        pathMatch: 'full',
        redirectTo: 'container-tracking/inventory?view=occupancy'
    },
    {
        path: 'container-tracking/yard-slots',
        pathMatch: 'full',
        redirectTo: 'container-tracking/setup/yard-slots'
    },
    {
        path: 'container-tracking/appointments',
        component: YardOperationsComponent,
        canActivate: [EnterpriseSubscriptionGuard, PermissionGuard],
        data: {
            mode: 'appointments',
            permission: app_permissions.container_tracking.yards.view,
            enterpriseModules: ['CONTAINER_TRACKING'],
            enterpriseFeatureLabel: 'Container Tracking'
        }
    },
    {
        path: 'container-tracking/eirs',
        component: YardOperationsComponent,
        canActivate: [EnterpriseSubscriptionGuard, PermissionGuard],
        data: {
            mode: 'eirs',
            permission: app_permissions.container_tracking.yards.view,
            enterpriseModules: ['CONTAINER_TRACKING'],
            enterpriseFeatureLabel: 'Container Tracking'
        }
    },
    {
        path: 'container-tracking/charges',
        pathMatch: 'full',
        redirectTo: 'container-tracking/setup'
    },
    {
        path: 'container-tracking/cases',
        component: ContainerTrackingOverviewComponent,
        canActivate: [EnterpriseSubscriptionGuard, PermissionGuard],
        data: {
            permission: app_permissions.container_tracking.cases.view,
            enterpriseModules: ['CONTAINER_TRACKING'],
            enterpriseFeatureLabel: 'Container Tracking'
        }
    },
    {
        path: 'container-tracking/cases/add',
        component: ContainerCaseEditorComponent,
        canActivate: [EnterpriseSubscriptionGuard, PermissionGuard],
        data: {
            permission: app_permissions.container_tracking.cases.create,
            enterpriseModules: ['CONTAINER_TRACKING'],
            enterpriseFeatureLabel: 'Container Tracking'
        }
    },
    {
        path: 'container-tracking/bookings/view',
        component: ContainerBookingWorkspaceComponent,
        canActivate: [EnterpriseSubscriptionGuard, PermissionGuard],
        data: {
            permission: app_permissions.container_tracking.cases.view,
            enterpriseModules: ['CONTAINER_TRACKING'],
            enterpriseFeatureLabel: 'Container Tracking'
        }
    },
    {
        path: 'container-tracking/cases/view/:caseId/storage-legs/:storageLegId',
        component: ContainerStorageLegOperationsComponent,
        canActivate: [EnterpriseSubscriptionGuard, PermissionGuard],
        data: {
            permission: app_permissions.container_tracking.cases.view,
            enterpriseModules: ['CONTAINER_TRACKING'],
            enterpriseFeatureLabel: 'Container Tracking'
        }
    },
    {
        path: 'container-tracking/cases/view/:id',
        component: ContainerCaseDetailComponent,
        canActivate: [EnterpriseSubscriptionGuard, PermissionGuard],
        data: {
            permission: app_permissions.container_tracking.cases.view,
            enterpriseModules: ['CONTAINER_TRACKING'],
            enterpriseFeatureLabel: 'Container Tracking'
        }
    },
    {
        path: 'container-tracking/yard',
        pathMatch: 'full',
        redirectTo: 'container-tracking/setup'
    }
];
